import { z } from 'zod';
import {
  CARGO_PROBLEM_DESCRIPTION_MAX,
  CargoProblemCategorySchema,
  CargoProblemQueueSchema,
  CargoProblemTransitionActionSchema,
} from './vocab.js';
import { CargoProblemActorSchema } from './wire.js';

/**
 * @aiPurpose the author's create contract for the workspace flow: a problem is
 *   filed into a queue AND handed to one named person, so nobody has to guess
 *   who owns it.
 * @aiInvariant `assigneeId` is required HERE, on the new API flow. The database
 *   column stays nullable — legacy rows and the shipped
 *   `CreateCargoProblemInputSchema` remain valid, and no destructive NOT NULL
 *   is introduced.
 */
export const CreateCargoProblemWorkspaceInputSchema = z
  .object({
    cargoArticleId: z.string().uuid(),
    category: CargoProblemCategorySchema,
    queue: CargoProblemQueueSchema,
    description: z.string().trim().min(1).max(CARGO_PROBLEM_DESCRIPTION_MAX),
    assigneeId: z.string().uuid(),
  })
  .strict();
export type CreateCargoProblemWorkspaceInput = z.infer<
  typeof CreateCargoProblemWorkspaceInputSchema
>;

/**
 * @aiPurpose the one body that names a responsible person — used by the new
 *   submit/resubmit path and by a post-claim reassignment alike, because both
 *   express the same intent and both restart the reaction cycle.
 */
export const CargoProblemAssigneeInputSchema = z.object({ assigneeId: z.string().uuid() }).strict();
export type CargoProblemAssigneeInput = z.infer<typeof CargoProblemAssigneeInputSchema>;

/**
 * @aiPurpose the roster a problem may be assigned FROM, resolved from the users
 *   directory rather than from existing rows so a newly-hired logist is
 *   selectable on day one.
 * @aiSecurity exposes only `{ id, name }` — the display name every timeline row
 *   already carries. No email, phone, role, or active flag.
 */
export const CargoProblemAssigneeCandidatesResponseSchema = z
  .object({ candidates: z.array(CargoProblemActorSchema) })
  .strict();
export type CargoProblemAssigneeCandidatesResponse = z.infer<
  typeof CargoProblemAssigneeCandidatesResponseSchema
>;

/**
 * @aiPurpose what THIS user still has to look at on one problem — the row a
 *   list entry renders its badge from.
 * @aiInvariant `unreadCount` counts messages strictly newer than the user's
 *   read pointer, falling back to the rollout baseline when no pointer exists —
 *   which is why history written before the workspace shipped starts read.
 *   `actionRequired` is the separate "the next workflow step is yours" signal,
 *   so a read problem still surfaces when it is waiting on this user.
 * @aiPii content-free by construction: an id, a count, and a boolean. No author
 *   names, no message text, and no read timestamp — the exact read pointer
 *   comes back only to the user who moved it, on the read response.
 */
export const CargoProblemAttentionItemSchema = z
  .object({
    problemId: z.string().uuid(),
    unreadCount: z.number().int().nonnegative(),
    actionRequired: z.boolean(),
  })
  .strict();
export type CargoProblemAttentionItem = z.infer<typeof CargoProblemAttentionItemSchema>;

/**
 * @aiPurpose the whole personal attention surface in one response: per-problem
 *   rows plus the single number the navigation badge renders.
 * @aiInvariant `menuCount` counts PROBLEMS, not signals — a problem that is
 *   both unread and action-required moves it by one. It is therefore not
 *   derivable by summing `unreadCount`, which is why the server sends it.
 * @aiInvariant `items` holds only problems that currently need attention, and
 *   only those the actor can still see; an invisible problem is omitted from
 *   both the array and the count, so a private DRAFT cannot leak through the
 *   badge.
 */
export const CargoProblemAttentionResponseSchema = z
  .object({
    items: z.array(CargoProblemAttentionItemSchema),
    menuCount: z.number().int().nonnegative(),
  })
  .strict();
export type CargoProblemAttentionResponse = z.infer<typeof CargoProblemAttentionResponseSchema>;

/**
 * @aiPurpose body for advancing the personal read pointer.
 * @aiInvariant the client sends the newest visible message id, never a
 *   timestamp: the server reads the persisted row's time and advances the
 *   pointer monotonically, so two devices cannot move it backwards.
 */
export const CargoProblemReadInputSchema = z.object({ messageId: z.string().uuid() }).strict();
export type CargoProblemReadInput = z.infer<typeof CargoProblemReadInputSchema>;

export const CargoProblemReadResponseSchema = z
  .object({
    lastReadAt: z.string().datetime(),
    unreadCount: z.number().int().nonnegative(),
  })
  .strict();
export type CargoProblemReadResponse = z.infer<typeof CargoProblemReadResponseSchema>;

/**
 * @aiPurpose the single step this actor is expected to take next on this
 *   problem, so the workspace renders one primary button instead of inferring
 *   it from status × role on the client.
 * @aiInvariant total by construction: every status × role shape resolves to one
 *   literal, and `none` is the explicit answer for "this actor has no next
 *   step" (a terminal problem, an onlooker, a rejected problem seen by the
 *   assignee). The workflow literals are the shipped
 *   `CargoProblemTransitionActionSchema` values, not a parallel vocabulary —
 *   a resubmit is the author's `submit` on a REJECTED problem.
 */
export const CargoProblemWorkspaceNextActionSchema = z.enum([
  'none',
  ...CargoProblemTransitionActionSchema.options,
]);
export type CargoProblemWorkspaceNextAction = z.infer<typeof CargoProblemWorkspaceNextActionSchema>;

/**
 * @aiPurpose the exhaustive capability set the workspace shell renders from.
 *   One role-stable layout; only these flags and `nextAction` differ.
 * @aiInvariant a NEW shape, not an extension of `CargoProblemCapabilitiesSchema`
 *   — that one is still parsed strictly by clients loaded from the previous
 *   color. `canPostMessage` is the workspace spelling of the legacy
 *   `canComment`; `canReassign` and `canDeleteAttachment` map to the two
 *   permissions whose seeded grants land only after the production fence.
 * @aiInvariant `nextAction` is required, never null: an absent descriptor would
 *   push the status × role decision back into the client, which is the thing
 *   this endpoint exists to prevent.
 */
export const CargoProblemWorkspaceCapabilitiesSchema = z
  .object({
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    canSubmit: z.boolean(),
    canReject: z.boolean(),
    canClaim: z.boolean(),
    canSubmitReview: z.boolean(),
    canEvaluate: z.boolean(),
    canPostMessage: z.boolean(),
    canAttach: z.boolean(),
    canReply: z.boolean(),
    canReassign: z.boolean(),
    canDeleteAttachment: z.boolean(),
    nextAction: CargoProblemWorkspaceNextActionSchema,
  })
  .strict();
export type CargoProblemWorkspaceCapabilities = z.infer<
  typeof CargoProblemWorkspaceCapabilitiesSchema
>;

export const CargoProblemWorkspaceCapabilitiesResponseSchema = z
  .object({ capabilities: CargoProblemWorkspaceCapabilitiesSchema })
  .strict();
export type CargoProblemWorkspaceCapabilitiesResponse = z.infer<
  typeof CargoProblemWorkspaceCapabilitiesResponseSchema
>;
